import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowSync24Filled, Warning48Filled } from '@fluentui/react-icons';
import useAppUpdateCheck from '../../../hooks/useAppUpdateCheck';
import styles from './AppUpdateModal.module.css';

const isMacPlatform = () => {
    if (typeof navigator === 'undefined') return false;
    const platform = navigator.userAgentData?.platform || navigator.platform || '';
    const ua = navigator.userAgent || '';
    return /Mac|iPhone|iPad|iPod/i.test(platform) || /Mac OS X/i.test(ua);
};

const Key = ({ children }) => <kbd className={styles.key}>{children}</kbd>;

const Shortcut = ({ keys }) => (
    <span className={styles.shortcut}>
        {keys.map((key, index) => (
            <React.Fragment key={`${key}-${index}`}>
                {index > 0 ? <span className={styles.keyPlus}>+</span> : null}
                <Key>{key}</Key>
            </React.Fragment>
        ))}
    </span>
);

/**
 * Soft update prompt when a new frontend or backend deploy is detected.
 * Dismissible; leaves a reminder banner until the user hard-refreshes.
 */
const AppUpdateModal = () => {
    const { showModal, showBanner, dismissModal, openModal, refreshNow } = useAppUpdateCheck();
    const isMac = React.useMemo(() => isMacPlatform(), []);

    React.useEffect(() => {
        if (!showModal) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [showModal]);

    const dialog =
        showModal && typeof document !== 'undefined'
            ? createPortal(
                  <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="app-update-title">
                      <div className={styles.panel}>
                          <div className={styles.icon}>
                              <Warning48Filled color="var(--Brand-Primary)" />
                          </div>
                          <h5 id="app-update-title" className={styles.title}>
                              Update Available
                          </h5>
                          <p className={styles.message}>
                              A new version of Lens is available. Unsaved work may be affected — save your changes, then
                              hard refresh to load the latest version, or use the button below.
                          </p>
                          <div className={styles.shortcuts}>
                              <div className={styles.shortcutRow}>
                                  <Shortcut keys={isMac ? ['⌘', 'Shift', 'R'] : ['Ctrl', 'Shift', 'R']} />
                              </div>
                          </div>
                          <div className={styles.actions}>
                              <button type="button" className={styles.primaryBtn} onClick={refreshNow}>
                                  Refresh now
                              </button>
                              <button type="button" className={styles.secondaryBtn} onClick={dismissModal}>
                                  Later
                              </button>
                          </div>
                      </div>
                  </div>,
                  document.body,
              )
            : null;

    return (
        <>
            {showBanner && !showModal ? (
                <div className={styles.banner} role="status">
                    <ArrowSync24Filled />
                    <p className={styles.bannerText}>A new version of Lens is available.</p>
                    <button type="button" className={styles.bannerRefresh} onClick={refreshNow}>
                        Refresh
                    </button>
                    <button type="button" className={styles.bannerLater} onClick={openModal}>
                        Details
                    </button>
                </div>
            ) : null}
            {dialog}
        </>
    );
};

export default AppUpdateModal;
